import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export type Trigger =
  | { type: "cron"; cron: string; tz?: string }
  | { type: "interval"; minutes: number }
  | { type: "once"; at: string; tz?: string }
  | { type: "webhook"; allow_machine_tools?: boolean }
  | { type: "manual" };

export interface Task {
  action: "agent" | "notify" | "checkin";
  message: string | null;
  condition: { sql: string; when: "rows" | "none" } | null;
  id: string;
  name: string;
  instructions: string;
  model_id: string | null;
  folder_id: string | null;
  trigger: Trigger;
  trigger_text: string;
  mcp_servers: string[] | null;
  channels: string[] | null;
  memory_enabled: boolean;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  last_status: string | null;
  webhook_url: string | null;
  created_at: string;
}

export interface TaskRun {
  id: string;
  task_id: string;
  chat_id: string | null;
  status: "queued" | "starting" | "running" | "done" | "error";
  trigger: string;
  event: Record<string, unknown> | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  notified: boolean;
  created_at: string;
}

export interface Notification {
  id: string;
  task_id: string | null;
  task_run_id: string | null;
  title: string;
  body: string;
  priority: "low" | "normal" | "high";
  read_at: string | null;
  created_at: string;
}

export interface Channel {
  id: string;
  plugin: string;
  name: string;
  config: Record<string, unknown>;
  enabled: boolean;
}

export interface NotifierPlugin {
  id: string;
  label: string;
  description: string;
  schema: { properties?: Record<string, { type?: string; description?: string; default?: unknown; secret?: boolean; title?: string }>; required?: string[] };
}

export function useTasks() {
  return useQuery({ queryKey: ["tasks"], queryFn: () => api.get<Task[]>("/api/tasks") });
}

export function useChannels() {
  return useQuery({ queryKey: ["channels"], queryFn: () => api.get<Channel[]>("/api/notification-channels") });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => api.get<{ count: number }>("/api/notifications/unread-count").then((r) => r.count),
    staleTime: 60_000,
  });
}

export function relTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (d.getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < 60) return fmt.format(Math.round(diff), "second");
  if (abs < 3600) return fmt.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return fmt.format(Math.round(diff / 3600), "hour");
  if (abs < 86400 * 7) return fmt.format(Math.round(diff / 86400), "day");
  return d.toLocaleString();
}
